import { ComponentType, FC } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { useTheme } from '@mui/material';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenWidth } from '@mgm/common/hooks/useResize';
import { TMultipleDaySelectionPicker } from './type';

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  isSelected: boolean;
  isHovered: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isHovered',
})<CustomPickerDayProps>(({
  theme, isSelected, isHovered,
}) => ({
  margin: 0,
  marginRight: 'auto',
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main,
    },
  }),
  ...(isHovered && {
    backgroundColor: theme.palette.primary[theme.palette.mode],
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary[theme.palette.mode],
    },
  }),
})) as ComponentType<CustomPickerDayProps>;

function Day(
  props: PickersDayProps<Dayjs> & {
    dates: Array<string>;
  },
) {
  const {
    day,
    dates,
    outsideCurrentMonth,
    ...other
  } = props;

  const isSelected = !outsideCurrentMonth
    && dates.includes(day.format('YYYY-MM-DD'));

  return (
    <CustomPickersDay
      outsideCurrentMonth
      {...other}
      day={day}
      disableMargin
      selected={false}
      isSelected={isSelected}
      isHovered={false}
    />
  );
}

const MultipleDaySelectionPicker: FC<TMultipleDaySelectionPicker> = ({
  dates,
  setDates,
  width,
}) => {
  const theme = useTheme() as TCustomTheme;
  const { screenWidth } = useScreenWidth();
  const { isMobile } = useCustomTheme(theme);
  const findIndexDate = (dates: Array<string>, date: Dayjs) => dates.findIndex((item) => {
    const compareDate = dayjs(item, 'YYYY-MM-DD').startOf('day');

    return dayjs(compareDate).isSame(dayjs(date));
  });

  const handleChange = (newVal: Dayjs | null) => {
    const array = [...dates];
    const date = dayjs(newVal).startOf('day');
    const index = findIndexDate(array, date);

    if (index >= 0) {
      array.splice(index, 1);
    } else {
      array.push(date.format('YYYY-MM-DD'));
    }

    setDates(array, date);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateCalendar
        onChange={handleChange}
        minDate={dayjs()}
        showDaysOutsideCurrentMonth
        slots={{ day: Day as any }}
        sx={{
          width: isMobile ? screenWidth - 32 : width,
          border: '1px solid rgb(145, 158, 171, 0.24)',
          borderRadius: '8px',
          '& .MuiDayCalendar-weekDayLabel': {
            width: '100%',
          },
          '& .MuiDayCalendar-slideTransition>*': {
            ...(!isMobile && {
              left: '20px',
              right: '-20px',
            }),
          },
          '& .MuiDayCalendar-slideTransition': {
            minHeight: '200px',
          },
        }}
        slotProps={{
          day: () => ({
            dates,
          } as any),
        }}
      />
    </LocalizationProvider>
  );
};

export {
  MultipleDaySelectionPicker,
};
